import { useMemo, useState } from 'react';

import { RELOAD_CAL_MODAL } from '@/constants';
import { useCurrentUser } from '@/hooks';
import { Button, Dialog, Icon, TextField, Typography } from '@/ui';

import Cal from './embed';

import type { User } from '@/types';
import type { FC } from 'react';

import { usesMomentsScheduling } from '@/features/appointments/schedulingProvider';
import { useLazyGetUnavailabilityDiagnosticsQuery } from '@/store/api/sessionApi';
import { hasRole } from '@/types/user';

interface ScheduleSessionProps {
  callback?: () => void;
  client: User;
  close?: () => void;
  coach: User;
  eventTypeSlug?: string;
}

const ScheduleSession: FC<ScheduleSessionProps> = ({
  callback,
  client,
  coach,
  eventTypeSlug = '',
  close = undefined,
}) => {
  const { calendarUrl = '' } = coach.coachProfile ?? {};
  const { user: currentUser } = useCurrentUser();
  const newCalendarsEnabled = usesMomentsScheduling(coach);
  const showDiagnostics =
    import.meta.env.DEV &&
    (hasRole(currentUser, 'admin') || hasRole(currentUser, 'support'));
  const [slotStartsAtInput, setSlotStartsAtInput] = useState('');
  const [durationMinutesInput, setDurationMinutesInput] = useState('45');
  const [memberEmailInput, setMemberEmailInput] = useState(client.email || '');

  const [runDiagnostics, diagnosticsResult] =
    useLazyGetUnavailabilityDiagnosticsQuery();

  const diagnosticsErrorMessage = useMemo(() => {
    const err = diagnosticsResult.error as
      { data?: { error?: string }; status?: number } | undefined;
    if (!err) return '';
    return err.data?.error || 'Failed to run diagnostics for that slot.';
  }, [diagnosticsResult.error]);
  let diagnosticsSummary = 'No blockers found for this slot';
  if (diagnosticsResult.data?.complete === false) {
    diagnosticsSummary = 'Diagnostics incomplete; some blockers may be missing';
  } else if (diagnosticsResult.data?.blocked) {
    diagnosticsSummary = `${diagnosticsResult.data.blockers.length} blocker(s) found`;
  }

  const onClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!close) {
      setTimeout(() => window.location.reload(), RELOAD_CAL_MODAL);
    } else {
      close();
    }
  };

  const onRunDiagnostics = async () => {
    const startsAt = new Date(slotStartsAtInput);
    const duration = Number(durationMinutesInput);

    if (Number.isNaN(startsAt.getTime())) return;
    if (!Number.isFinite(duration) || duration <= 0) return;

    const endsAt = new Date(startsAt.getTime() + duration * 60 * 1000);
    await runDiagnostics({
      coachEmail: coach.email,
      memberEmail: memberEmailInput || undefined,
      slotEndsAt: endsAt.toISOString(),
      slotStartsAt: startsAt.toISOString(),
    });
  };

  return (
    <Dialog fullScreen open>
      <Button className="absolute right-4 top-4 z-10" onClick={onClose}>
        <Icon>close</Icon>
      </Button>
      {showDiagnostics && (
        <div className="absolute left-4 top-4 z-10 w-80 rounded-md border border-slate-200 bg-white/95 p-3 shadow-sm backdrop-blur">
          <Typography className="mb-2" variant="subtitle2">
            Slot diagnostics
          </Typography>
          <div className="flex flex-col gap-2">
            <TextField
              label="Slot start"
              size="small"
              type="datetime-local"
              value={slotStartsAtInput}
              onChange={(e) => setSlotStartsAtInput(e.target.value)}
            />
            <TextField
              label="Duration (minutes)"
              size="small"
              type="number"
              value={durationMinutesInput}
              onChange={(e) => setDurationMinutesInput(e.target.value)}
            />
            <TextField
              label="Member email context"
              size="small"
              value={memberEmailInput}
              onChange={(e) => setMemberEmailInput(e.target.value)}
            />
            <Button
              disabled={diagnosticsResult.isFetching}
              size="small"
              variant="outlined"
              onClick={onRunDiagnostics}
            >
              {diagnosticsResult.isFetching ? 'Checking...' : 'Check blockers'}
            </Button>
            {diagnosticsErrorMessage && (
              <Typography className="text-xs text-red-600">
                {diagnosticsErrorMessage}
              </Typography>
            )}
            {diagnosticsResult.data && (
              <div className="max-h-48 overflow-y-auto rounded border border-slate-200 bg-slate-50 p-2">
                <Typography className="mb-1 text-xs font-semibold text-slate-700">
                  {diagnosticsSummary}
                </Typography>
                {diagnosticsResult.data.blockers.map((blocker) => (
                  <div
                    key={`${blocker.bookingUid || blocker.sourceCalendarId || blocker.source}-${blocker.startsAt}-${blocker.endsAt}`}
                    className="mb-2 border-b border-slate-200 pb-2 last:mb-0 last:border-b-0 last:pb-0"
                  >
                    <Typography className="text-xs text-slate-700">
                      {blocker.blockingMemberName ||
                        blocker.blockingMemberEmail ||
                        'Unknown member'}
                    </Typography>
                    <Typography className="text-xs text-slate-600">
                      {blocker.eventTypeSlug || 'unknown-event'} •{' '}
                      {blocker.source}
                    </Typography>
                    <Typography className="text-xs text-slate-600">
                      {new Date(blocker.startsAt).toLocaleString()} -{' '}
                      {new Date(blocker.endsAt).toLocaleTimeString()}
                    </Typography>
                    {blocker.bookingUid && (
                      <Typography className="text-xs text-slate-600">
                        UID: {blocker.bookingUid}
                      </Typography>
                    )}
                    {blocker.hiddenInMemberContext && (
                      <Typography className="text-xs text-amber-700">
                        Hidden in selected member context
                      </Typography>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      <Cal
        callback={callback}
        calLink={`${calendarUrl}${eventTypeSlug ? `/${eventTypeSlug}` : ''}`}
        email={client.email}
        name={client.fullName}
        newCalendarsEnabled={newCalendarsEnabled}
      />
    </Dialog>
  );
};

export default ScheduleSession;
